import { Notification, SummaryRow, SummaryView } from '@salecycle/ui-component-library';
import { TFunction } from 'i18next';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateStepValidityAction } from '../../../actions';
import { useNewSftpUserDispatch, useNewSftpUserState } from '../../../context/useNewSftpUserContext';
import { getSteps, ISftpUser } from '../../../data/NewSftpUserConstants';
import { buildNewSftpUserPath } from '../../../utils/helpers';

export interface IProps {
  push: (href: string) => void;
  currentStep: number;
  isError?: boolean;
  errorMessage?: string;
}

/* eslint-disable react-hooks/exhaustive-deps */
export const SummaryStep: React.FC<IProps> = ({ currentStep, push, isError, errorMessage }: IProps) => {
  const { t } = useTranslation('sftpUserManagement');

  const [notifcationOpen, setNotificationOpen] = useState(isError);
  const { validSteps, sftpUser } = useNewSftpUserState();
  const dispatch = useNewSftpUserDispatch();

  const steps = getSteps(t);

  useEffect(() => {
    updateStepValidityAction(true, validSteps, currentStep, dispatch);

    return () => toggleNotification(false);
  }, []);

  useEffect(() => {
    setNotificationOpen(isError);
  }, [isError]);

  const handleUserEditClick = () => {
    push(buildNewSftpUserPath(steps[0].route));
  };

  const toggleNotification = (isOpen: boolean) => {
    setNotificationOpen(isOpen);
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return (
    <>
      <Notification isOpen={notifcationOpen} variation="error" onChange={toggleNotification}>
        {errorMessage}
      </Notification>

      <SummaryView heading={t('newSftpUser.summaryStep.heading')} subHeading={t('newSftpUser.summaryStep.subHeading')}>
        <SummaryRow
          heading={t('newSftpUser.summaryStep.newSftpUserRow.heading')}
          description={getUserText(sftpUser, t)}
          buttonText={t('newSftpUser.summaryStep.newSftpUserRow.button')}
          onClick={handleUserEditClick}
          data-testid="summary-user"
        />

        <SummaryRow
          heading={t('newSftpUser.summaryStep.emailRow.heading')}
          description={getEmailText(sftpUser, t)}
          buttonText={t('newSftpUser.summaryStep.emailRow.button')}
          onClick={handleUserEditClick}
          data-testid="summary-email"
        />

        <SummaryRow
          heading={t('newSftpUser.summaryStep.newSftpSitesRow.heading')}
          description={getSitesText(sftpUser, t)}
          buttonText={t('newSftpUser.summaryStep.newSftpSitesRow.button')}
          onClick={handleUserEditClick}
          data-testid="summary-sites"
        />
      </SummaryView>
    </>
  );
};

const getUserText = (sftpUser: ISftpUser, t: TFunction): string => {
  return t('newSftpUser.summaryStep.newSftpUserRow.description', {
    name: sftpUser.username,
  });
};

const getEmailText = (sftpUser: ISftpUser, t: TFunction): string => {
  return t('newSftpUser.summaryStep.emailRow.description', {
    email: sftpUser.email,
  });
};

const getSitesText = (sftpUser: ISftpUser, t: TFunction): string => {
  return t('newSftpUser.summaryStep.newSftpSitesRow.description', {
    name: sftpUser.username,
    sites: sftpUser.sites
      ?.map(v => v.label)
      .join(', ')
      .replace(/,([^,]*)$/, ' &$1'),
  });
};

export default SummaryStep;
